import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getUserPoints, calculateReputationScore } from '../../services/pointsService';
import { getUserBadges, BadgeDefinition } from '../../services/badgeService';
import BadgeDisplay from '../badges/BadgeDisplay';
import './PointsDisplay.css';

interface PointsDisplayProps {
  userId?: string; // If not provided, shows current user's points
  minimal?: boolean; // If true, shows a minimal version for navigation bars
}

// Global cache to prevent duplicate requests across all instances
const globalCache = {
  pointsData: null as any,
  badgeData: {} as Record<string, BadgeDefinition[]>,
  pointsPromise: null as Promise<any> | null,
  badgePromises: {} as Record<string, Promise<any>>,
  lastFetch: 0,
  CACHE_DURATION: 30000, // 30 seconds
};

const PointsDisplay: React.FC<PointsDisplayProps> = ({ userId, minimal = false }) => {
  const [pointsData, setPointsData] = useState<{
    points: number;
    reputationScore: number;
    badges: string[];
  } | null>(globalCache.pointsData);
  const [badgeData, setBadgeData] = useState<BadgeDefinition[]>(
    userId ? globalCache.badgeData[userId] || [] : []
  );
  const [loading, setLoading] = useState(!globalCache.pointsData);
  const [error, setError] = useState<string | null>(null);

  // Component-level refs to track mounting state
  const isMounted = useRef(true);
  const hasInitialized = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchPointsData = useCallback(async () => {
    const now = Date.now();

    // Check if we have fresh cached data
    if (globalCache.pointsData && now - globalCache.lastFetch < globalCache.CACHE_DURATION) {
      if (isMounted.current) {
        setPointsData(globalCache.pointsData);
        setLoading(false);
      }
      return;
    }

    // Check if there's already a request in progress
    if (globalCache.pointsPromise !== null) {
      try {
        const data = await globalCache.pointsPromise;
        if (isMounted.current) {
          setPointsData(data);
          setLoading(false);
        }
      } catch (err: any) {
        if (isMounted.current) {
          setError(err.message || 'Failed to load points data');
          setLoading(false);
        }
      }
      return;
    }

    // Start new request
    if (isMounted.current) {
      setLoading(true);
    }

    globalCache.pointsPromise = getUserPoints();

    try {
      const data = await globalCache.pointsPromise;
      globalCache.pointsData = data;
      globalCache.lastFetch = now;

      if (isMounted.current) {
        setPointsData(data);
        setError(null);
        setLoading(false);
      }
    } catch (err: any) {
      if (isMounted.current) {
        setError(err.message || 'Failed to load points data');
        setLoading(false);
      }
    } finally {
      globalCache.pointsPromise = null;
    }
  }, []);

  const fetchUserBadges = useCallback(async () => {
    if (!userId) return;

    // Check if we have cached data for this user
    if (globalCache.badgeData[userId]) {
      if (isMounted.current) {
        setBadgeData(globalCache.badgeData[userId]);
      }
      return;
    }

    // Check if there's already a request in progress for this user
    if (userId in globalCache.badgePromises) {
      try {
        const data = await globalCache.badgePromises[userId];
        if (isMounted.current) {
          setBadgeData(data.badges);
        }
      } catch (err: any) {
        console.error('Failed to load user badges:', err);
      }
      return;
    }

    // Start new request
    globalCache.badgePromises[userId] = getUserBadges(userId);

    try {
      const data = await globalCache.badgePromises[userId];
      globalCache.badgeData[userId] = data.badges;

      if (isMounted.current) {
        setBadgeData(data.badges);
      }
    } catch (err: any) {
      console.error('Failed to load user badges:', err);
    } finally {
      delete globalCache.badgePromises[userId];
    }
  }, [userId]);

  // Initialize data fetching only once per component lifecycle
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    fetchPointsData();
    if (userId) {
      fetchUserBadges();
    }
  }, [fetchPointsData, fetchUserBadges, userId]);

  const handleRefreshReputation = async () => {
    if (!isMounted.current) return;

    try {
      setLoading(true);
      const data = await calculateReputationScore();
      globalCache.pointsData = data;
      globalCache.lastFetch = Date.now();

      if (isMounted.current) {
        setPointsData(data);
        setError(null);
      }
    } catch (err: any) {
      if (isMounted.current) {
        setError(err.message || 'Failed to calculate reputation');
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  if (loading && !pointsData) {
    return minimal ? (
      <div className="points-display-minimal loading">
        <span className="loading-spinner">⟳</span>
      </div>
    ) : (
      <div className="points-display-loading">
        <span className="loading-spinner">⟳</span> Loading points data...
      </div>
    );
  }

  if (error && !pointsData) {
    return minimal ? null : <div className="points-display-error">{error}</div>;
  }

  if (!pointsData) {
    return null;
  }

  if (minimal) {
    return (
      <div className="points-display-minimal">
        <span className="points-value" title="Your points">
          {pointsData.points}
        </span>
        <span className="points-label">pts</span>
        {badgeData.length > 0 && (
          <span className="badges-count" title={`${badgeData.length} badges earned`}>
            🏆 {badgeData.length}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="points-display">
      <div className="points-section">
        <h3>Points & Reputation</h3>

        <div className="points-stats">
          <div className="stat-item">
            <div className="stat-value">{pointsData.points}</div>
            <div className="stat-label">Total Points</div>
          </div>

          <div className="stat-item">
            <div className="stat-value">{pointsData.reputationScore}</div>
            <div className="stat-label">Reputation</div>
            <button
              className="refresh-reputation-btn"
              onClick={handleRefreshReputation}
              title="Recalculate reputation score"
              disabled={loading}
            >
              {loading ? '⟳' : '↻'}
            </button>
          </div>
        </div>

        <div className="badges-section">
          <h4>Badges ({badgeData.length})</h4>
          <BadgeDisplay badges={badgeData} size="medium" maxDisplay={6} />
        </div>
      </div>
    </div>
  );
};

export default React.memo(PointsDisplay);
